import itemQueries from '@apis/queries/itemQueries';
import AuthGuard, { AuthFlow } from '@components/AuthGuard';
import Badge from '@components/Badge';
import Column from '@components/Column';
import Image from '@components/Image';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import useAppCustom from '@hooks/useAppCustom';
import HeartFill from '@icons/favorite/HeartFill';
import { ShopInMapRow } from '@models/shop';
import { useFlow } from '@stackflow/react';
import { pressActionEffect } from '@styles/mixin';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import useShopFavorite from './hooks/useShopFavorite';

interface Props {
    isOrder?: boolean;
    shop: ShopInMapRow;
    originalItemId?: string;
}

function ShopItem({ shop, isOrder, originalItemId }:Props) {
    const { push } = useFlow();
    const { t } = useTranslation('shopList');
    const {
        name, badges, distanceFromUser, address, thumbnailImageUrl, orderStatus, isMyShop,
    } = shop;
    const { shopImageShape } = useAppCustom().shop;
    const { toggleMyShop } = useShopFavorite(shop);
    const { data: shopItem } = useQuery({
        ...itemQueries.shopItemFromShop(shop.id, originalItemId!),
        enabled: !!originalItemId,
    });
    const handleOpen = () => {
        if (shopItem) {
            push('ItemActivity', { itemId: shopItem.id, shopId: shop.id });
            return;
        }
        if (orderStatus === 'BREAK_TIME' || orderStatus === 'CLOSED') {
            push('ShopPrepareModal', { shopId: shop.id });
        } else if (isOrder) {
            push('ShopLocationBottomSheet', {
                shopId: shop.id,
                lat: shop.lat,
                lng: shop.lon,
            });
        } else if (orderStatus === 'OPEN') {
            push('ShopDetailBottomSheet', { shopId: shop.id });
        }
    };
    const flows: AuthFlow[] = [
        { activityName: 'ShopListActivity', activityParams: { type: 'LIST' } },
    ];
    return (
        <Container spacing={4}
            onClick={handleOpen}
            fullWidth
            align='flex-start'
            aria-label='shop-item'
            css={pressActionEffect} >
            <ThumbnailContainer shape={shopImageShape} >
                <Thumbnail src={thumbnailImageUrl}
                    shape={shopImageShape}
                    alt={name}
                    width={200}
                    height={200}/>
                {orderStatus !== 'OPEN' && (
                    <Closed>
                        <Text fontWeight='bold'
                            color={colors.white}>
                            {t('shopItem.preparing')}
                        </Text>
                    </Closed>
                )}
            </ThumbnailContainer>
            <Content spacing={4}>
                <Column fullWidth spacing={1}>
                    <Row fullWidth
                        justify='space-between'
                        spacing={2} >
                        <Text size='title' fontWeight='medium' ellipsis css={css`flex: 1;`}>
                            {name}
                        </Text>
                        <AuthGuard css={css`width: 24px;`}
                            flows={flows} >
                            <div onClick={toggleMyShop}>
                                <HeartFill color={isMyShop ? 'var(--color-red)' : 'var(--color-icon-unselected)'}/>
                            </div>
                        </AuthGuard>
                    </Row>
                    <Text size='body-long' color='var(--color-text-secondary)'>
                        {address}
                    </Text>
                </Column>
                <Row align='center'
                    fullWidth
                    spacing={2} >
                    {distanceFromUser && (
                        <DistanceText color='var(--color-text-secondary)'
                            fontWeight='medium' >
                            {distanceFromUser}
                        </DistanceText>
                    )}
                    <BadgeContainer>
                        {badges.map((badge, index) => (
                            <Badge key={`${badge} ${index}`} >
                                {badge}
                            </Badge>
                        ))}
                    </BadgeContainer>
                </Row>
            </Content>
        </Container>
    );
}

const Container = styled(Row)`
    overflow: hidden;
    padding: var(--spacing-4) 0;
`;

const ThumbnailContainer = styled.div<{
    shape: 'CIRCLE' | 'RECTANGLE';
}>`
    align-items: center;
    border-radius: ${(props) => (props.shape === 'RECTANGLE' ? 'var(--radius-xs)' : 'var(--radius-full)')};
    display: flex;
    flex-shrink: 0;
    height: 100px;
    justify-content: center;
    overflow: hidden;
    position: relative;
    width: 100px;
`;

const Thumbnail = styled(Image)<{
    shape: 'CIRCLE' | 'RECTANGLE';
}>`
    height: 100%;
    object-fit: cover;
    width: 100%;
`;

const Closed = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(13, 15, 17, .4);
`;

const Content = styled(Column)`
    flex: 1;
    min-width: 0;
`;

const DistanceText = styled(Text)`
    white-space: nowrap;
`;

const BadgeContainer = styled.div`
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    gap: var(--spacing-1);
    min-width: 0;
`;

export default ShopItem;
